import { supabase } from './supabase';

// Mapeia os nomes antigos (PocketBase) para as tabelas do Supabase,
// para não precisar tocar em todas as chamadas espalhadas no App.js.
const TABLE = {
    prexo_produtos: 'produtos', produtos: 'produtos',
    prexo_embalagens: 'embalagens', embalagens: 'embalagens',
    prexo_insumos: 'insumos', insumos: 'insumos',
    prexo_canais: 'canais', canais: 'canais',
};

const CANAL_COLS = ['id_canal', 'nome', 'txHolding', 'txFixa', 'txFixaBaixa', 'txFixaAlta', 'limiarTxFixa', 'txVariavel', 'ads', 'imposto', 'ativo'];
// Colunas gravadas por tabela (peso/dimensões para o cálculo de frete do ML).
const COLS = {
    produtos: ['nome', 'sku', 'custo', 'unidade', 'peso'],
    insumos: ['nome', 'sku', 'custo', 'unidade', 'peso'],
    embalagens: ['nome', 'sku', 'custo', 'unidade', 'peso', 'comprimento', 'largura', 'altura'],
    canais: CANAL_COLS
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isUuid = (v) => typeof v === 'string' && UUID_RE.test(v);

const getUserId = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    return user?.id || null;
};

/**
 * Busca todos os registros do usuário (RLS garante o isolamento) para uma "coleção".
 * Retorna null se a nuvem estiver vazia (App decide manter os dados locais).
 */
export const syncCollection = async (collectionName, localData) => {
    const table = TABLE[collectionName];
    if (!table) return localData;

    const uid = await getUserId();
    if (!uid) return localData;

    const { data, error } = await supabase
        .from(table)
        .select('*')
        .order('created_at', { ascending: false });

    if (error) {
        // eslint-disable-next-line no-console
        console.error(`Erro ao sincronizar ${table}:`, error.message);
        return localData;
    }
    if (!data || data.length === 0) return null;

    // canais: o app usa `id` como slug ('meli'/'shopee'); expomos id_canal como id
    // e guardamos o uuid em _pgId (não é usado para update — dedup é por slug).
    if (table === 'canais') {
        return data.map((c) => ({ ...c, id: c.id_canal, _pgId: c.id }));
    }
    return data;
};

const NUM_COLS = new Set(['custo', 'peso', 'comprimento', 'largura', 'altura']);
const buildPayload = (table, item, uid) => {
    const cols = COLS[table] || COLS.produtos;
    const payload = { user_id: uid };
    for (const col of cols) {
        if (item[col] !== undefined) {
            payload[col] = NUM_COLS.has(col) ? (Number(item[col]) || 0) : item[col];
        }
    }
    // canal.id é o slug -> vira id_canal
    if (table === 'canais' && payload.id_canal === undefined && item.id !== undefined) {
        payload.id_canal = item.id;
    }
    return payload;
};

/**
 * Salva (insert/update) um item. Item novo (id local_ ou numérico) => insert.
 * Item com id uuid => update. Canais fazem upsert por slug (id_canal).
 */
export const saveItem = async (collectionName, item) => {
    const table = TABLE[collectionName];
    if (!table) return;

    const uid = await getUserId();
    if (!uid) return;

    try {
        const payload = buildPayload(table, item, uid);

        if (table === 'canais') {
            const slug = payload.id_canal;
            const { data: existing } = await supabase
                .from('canais')
                .select('id')
                .eq('user_id', uid)
                .eq('id_canal', slug)
                .maybeSingle();

            if (existing) {
                const { error } = await supabase.from('canais').update(payload).eq('id', existing.id);
                if (error) throw error;
            } else {
                const { error } = await supabase.from('canais').insert(payload);
                if (error) throw error;
            }
            return;
        }

        if (isUuid(item.id)) {
            const { error } = await supabase.from(table).update(payload).eq('id', item.id);
            if (error) throw error;
        } else {
            const { error } = await supabase.from(table).insert(payload);
            if (error) throw error;
        }
    } catch (err) {
        // eslint-disable-next-line no-console
        console.error(`Erro ao salvar item em ${table}:`, err.message || err);
    }
};

/**
 * Remove um item por id (uuid). Ids locais (não sincronizados) são ignorados.
 */
export const deleteItem = async (collectionName, id) => {
    const table = TABLE[collectionName];
    if (!table || !isUuid(id)) return;

    const { error } = await supabase.from(table).delete().eq('id', id);
    if (error) {
        // eslint-disable-next-line no-console
        console.error(`Erro ao deletar item em ${table}:`, error.message);
    }
};
